import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import supabase from './supabaseClient';
import LoaderScreen from './components/LoaderScreen';

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [hasVenueOwner, setHasVenueOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: session, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session.session) {
          setIsAuthenticated(false);
          setHasProfile(false);
          return;
        }

        // User is authenticated, now check if they have a profile and admin status
        const { data: profile, error: profileError } = await supabase
          .from('Users')
          .select('*, venue_owner, admin_Pass')
          .eq('user_id', session.session.user.id)
          .single();

        if (profileError || !profile) {
          setIsAuthenticated(true);
          setHasProfile(false);
          return;
        }

        setIsAuthenticated(true);
        setHasProfile(true);
        setHasVenueOwner(!!String(profile.venue_owner || '').trim());
        setIsAdmin(!!profile.admin_Pass);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
        setHasProfile(false);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null || hasProfile === null) {
    return <LoaderScreen subtitle="Checking your access" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If user is authenticated but has no profile, and they're not already on create-profile page
  if (isAuthenticated && !hasProfile && location.pathname !== '/create-profile') {
    return <Navigate to="/create-profile" />;
  }

  // Only allow admin and venue owner access - deny regular users
  if (isAuthenticated && hasProfile && !isAdmin && !hasVenueOwner) {
    return <Navigate to="/login" />;
  }

  // If the user is a venue owner, keep them on the venue owner portal and
  // prevent access to regular user pages guarded by this component
  if (isAuthenticated && hasProfile && hasVenueOwner && location.pathname !== '/venue-owner') {
    return <Navigate to="/venue-owner" />;
  }

  return children;
};

export default ProtectedRoute;
